import { useMemo } from 'react'
import type { ReactNode } from 'react'

import type { AiFinding, AiFindingDetail, AiFindingEvent } from '../../lib/api/aiFindings.ts'
import {
  canDecide,
  formatConfidence,
  isEvidence,
  recommendedActionLabel,
  verdictPresentation,
} from '../../lib/ai/semantics.ts'
import { mitreUrl } from '../../lib/detections/semantics.ts'
import { formatClock, formatClockShort, formatDayClock, formatInt } from '../../lib/format.ts'
import { Link } from '../../lib/router.tsx'
import { Button } from '../primitives/Button.tsx'
import { Notice } from '../primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../primitives/table.ts'
import { SeverityBadge } from '../detections/Severity.tsx'
import { CitationBadges, FindingStatusBadge, VerdictBadge } from './Badges.tsx'
import { findingHref } from './EventAiPanel.tsx'

/**
 * O dossiê de um achado. A hipótese é a manchete; abaixo, o que a sustenta
 * (confiança, de onde vem a pontuação, a fita da janela) e, por último, a
 * decisão. Todo texto vindo da IA ou da telemetria é texto puro.
 */
export function FindingDossier({
  detail,
  tenantName,
  onOpenEvent,
  onDecide,
}: {
  detail: AiFindingDetail
  tenantName: string | null
  onOpenEvent: (event: AiFindingEvent) => void
  /** Nulo no cliente: os botões não existem. */
  onDecide: ((decision: 'confirmed' | 'dismissed') => void) | null
}) {
  const { finding, events, events_truncated } = detail
  const confidence = formatConfidence(finding.confidence)
  const action = recommendedActionLabel(finding.recommended_action)

  return (
    <article className="flex min-h-0 flex-1 flex-col">
      {/* `key`: outro achado começa do topo, não da rolagem do anterior. */}
      <div key={finding.id} className="min-h-0 flex-1 overflow-y-auto">
        <header className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-line-subtle px-4 py-2.5">
          <VerdictBadge verdict={finding.verdict} size="lg" />
          {finding.severity !== null && <SeverityBadge severity={finding.severity} size="lg" />}
          <FindingStatusBadge status={finding.status} />
          <Link href={`/logs/${finding.endpoint_id}`} className="font-mono text-sm text-fg hover:underline">
            {finding.host}
          </Link>
          {tenantName !== null && <span className="text-sm text-fg-muted">{tenantName}</span>}
          <span className="tnum font-mono text-sm text-fg-secondary">
            {formatDayClock(finding.window_start)}–{formatClockShort(finding.window_end)} UTC
          </span>
          {finding.related_finding_id !== null && (
            <Link
              href={findingHref(finding.related_finding_id)}
              className="ml-auto text-sm text-accent underline-offset-2 hover:underline"
            >
              ← achado anterior da mesma máquina
            </Link>
          )}
        </header>

        <div className="flex flex-col gap-4 px-4 py-3">
          {finding.hypothesis !== null && (
            <p className="text-lg leading-snug whitespace-pre-wrap text-fg">{finding.hypothesis}</p>
          )}

          {finding.status === 'analysis_failed' && (
            <Notice tone="crit">
              <span className="whitespace-pre-wrap">Falha na análise: {finding.failure_reason ?? 'sem motivo informado'}</span>
            </Notice>
          )}
          {finding.status === 'awaiting_analysis' && (
            <Notice tone="warn">Selecionado para a IA, mas a análise está desligada.</Notice>
          )}
          {finding.status === 'triage_only' && (
            <Notice tone="neutral">Teve sinal na triagem, mas a pontuação não levou a máquina à IA.</Notice>
          )}

          <div className="grid grid-cols-1 gap-px bg-line-subtle sm:grid-cols-3">
            <Tile label="Confiança da IA">
              {confidence !== null ? (
                <>
                  <span className="tnum font-mono text-lg text-fg">{confidence}</span>
                  <span aria-hidden className="mt-1.5 block h-1 bg-inset">
                    <span
                      className={`block h-1 ${verdictPresentation(finding.verdict)?.fill ?? 'bg-line-strong'}`}
                      style={{ width: confidence }}
                    />
                  </span>
                </>
              ) : (
                <span className="text-sm text-fg-muted">sem veredito</span>
              )}
            </Tile>
            <Tile label={`Triagem · ${formatInt(finding.triage_score)} pts`}>
              <ScoreBar finding={finding} />
            </Tile>
            <Tile label="Ação sugerida">
              <span className="text-base text-fg">{action ?? 'nenhuma'}</span>
              {finding.recommended_action === 'isolate_host' && onDecide !== null && (
                <Link
                  href={`/logs/${finding.endpoint_id}`}
                  className="mt-0.5 block text-sm text-accent underline-offset-2 hover:underline"
                >
                  Centro de Controle da máquina →
                </Link>
              )}
            </Tile>
          </div>

          <EvidenceTape finding={finding} events={events} truncated={events_truncated} onOpen={onOpenEvent} />

          {finding.narrative !== null && (
            <p className="max-w-[72ch] text-base leading-relaxed whitespace-pre-wrap text-fg-secondary">
              {finding.narrative}
            </p>
          )}

          {finding.mitre_techniques.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {finding.mitre_techniques.map((id) => {
                const url = mitreUrl(id)
                const chip = 'border border-line px-1.5 py-px font-mono text-micro'
                return url === null ? (
                  <span key={id} className={`${chip} text-fg-secondary`}>
                    {id}
                  </span>
                ) : (
                  <a key={id} href={url} target="_blank" rel="noreferrer noopener" className={`${chip} text-accent hover:border-accent`}>
                    {id} ↗
                  </a>
                )
              })}
            </div>
          )}

          <SignalsTable finding={finding} />
          <EventsTable events={events} onOpen={onOpenEvent} />
        </div>
      </div>

      <footer className="flex shrink-0 flex-wrap items-center gap-2 border-t border-line-subtle px-4 py-2">
        {onDecide !== null && (
          <>
            <Button disabled={!canDecide(finding.status)} onClick={() => onDecide('dismissed')}>
              Descartar <Kbd>D</Kbd>
            </Button>
            <Button variant="danger" disabled={!canDecide(finding.status)} onClick={() => onDecide('confirmed')}>
              Confirmar incidente <Kbd>C</Kbd>
            </Button>
          </>
        )}
        <span className="ml-auto text-micro text-fg-muted">
          Análise feita por IA, verifique os fatos.
          {finding.analyzer !== null && (
            <span className="font-mono">
              {' '}
              {finding.analyzer}
            </span>
          )}
        </span>
      </footer>
    </article>
  )
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="border border-line px-1 font-mono text-micro text-fg-muted normal-case">{children}</kbd>
}

function Tile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col bg-surface px-3 py-2">
      <span className="mb-1 text-micro uppercase tracking-label text-fg-muted">{label}</span>
      {children}
    </div>
  )
}

/** De onde vêm os pontos: um segmento por sinal, largura pelo peso. */
function ScoreBar({ finding }: { finding: AiFinding }) {
  const signals = [...finding.triage_signals].sort((a, b) => b.weight - a.weight)
  const total = signals.reduce((sum, s) => sum + Math.max(0, s.weight), 0)
  if (total === 0) return <span className="text-sm text-fg-muted">sem sinais</span>
  const shades = ['bg-seq-2', 'bg-seq-3', 'bg-seq-4', 'bg-seq-5']
  return (
    <>
      <span className="mt-1 flex h-2 gap-px">
        {signals.map((s, i) => (
          <span
            key={s.code}
            title={`${s.code} · ${s.weight}`}
            className={shades[Math.min(i, shades.length - 1)]}
            style={{ flexGrow: Math.max(0, s.weight) }}
          />
        ))}
      </span>
      <span className="mt-1 truncate font-mono text-micro text-fg-muted">
        {signals.map((s) => s.code).join(' · ')}
      </span>
    </>
  )
}

/**
 * A janela inteira numa linha: cada evento citado no seu instante. Círculo é
 * evidência da IA; losango, só sinal da triagem. Clicar abre o evento.
 */
function EvidenceTape({
  finding,
  events,
  truncated,
  onOpen,
}: {
  finding: AiFinding
  events: readonly AiFindingEvent[]
  truncated: boolean
  onOpen: (event: AiFindingEvent) => void
}) {
  const start = Date.parse(finding.window_start)
  const end = Date.parse(finding.window_end)
  const span = Math.max(1, end - start)
  const marks = useMemo(
    () =>
      events.map((event) => ({
        event,
        x: Math.min(100, Math.max(0, ((Date.parse(event.event_time) - start) / span) * 100)),
        evidence: isEvidence(event.cited_as),
      })),
    [events, start, span],
  )
  const middle = new Date(start + span / 2).toISOString()

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-3 text-micro text-fg-muted">
        <span className="uppercase tracking-label">Janela</span>
        <span className="inline-flex items-center gap-1">
          <span aria-hidden className="inline-block size-2 rounded-full bg-crit" /> evidência da IA
        </span>
        <span className="inline-flex items-center gap-1">
          <span aria-hidden className="inline-block size-2 rotate-45 bg-seq-3" /> sinal da triagem
        </span>
        {truncated && <span className="text-warn">a leitura parou no limite: pode faltar evento</span>}
      </div>
      <div className="relative h-8 border-b border-line-strong bg-inset">
        {events.length === 0 && (
          <span className="absolute inset-0 flex items-center justify-center text-micro text-fg-muted">
            nenhum evento citado
          </span>
        )}
        {marks.map(({ event, x, evidence }) => (
          <button
            key={`${event.log_id}-${event.event_key}`}
            type="button"
            onClick={() => onOpen(event)}
            title={`${formatClock(event.event_time)} UTC · ${event.event_type}${
              event.process_name !== null ? ` · ${event.process_name}` : ''
            }`}
            aria-label={`Abrir evento das ${formatClock(event.event_time)} UTC`}
            className={`absolute -translate-x-1/2 hover:scale-150 ${
              evidence ? 'bottom-3 size-2.5 rounded-full bg-crit' : 'bottom-1 size-2 rotate-45 bg-seq-3'
            }`}
            style={{ left: `${x}%`, transition: 'transform var(--dur-fast) var(--ease-swift)' }}
          />
        ))}
      </div>
      <div className="flex justify-between tnum font-mono text-micro text-fg-muted">
        <span>{formatClockShort(finding.window_start)}</span>
        <span>{formatClockShort(middle)}</span>
        <span>{formatClockShort(finding.window_end)}</span>
      </div>
    </div>
  )
}

function SignalsTable({ finding }: { finding: AiFinding }) {
  if (finding.triage_signals.length === 0) return null
  return (
    <section>
      <h3 className="mb-1 text-micro font-medium uppercase tracking-label text-fg-secondary">Sinais da triagem</h3>
      <table className={TABLE}>
        <thead className={THEAD}>
          <tr>
            <th className={TH}>Sinal</th>
            <th className={`${TH} text-right`}>Peso</th>
            <th className={TH}>Por quê</th>
          </tr>
        </thead>
        <tbody>
          {finding.triage_signals.map((signal) => (
            <tr key={signal.code} className={TR}>
              <td className={`${TD} font-mono text-fg`}>{signal.code}</td>
              <td className={`${TD} text-right tnum font-mono`}>{formatInt(signal.weight)}</td>
              <td className={`${TD} whitespace-pre-wrap`}>{signal.explanation}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

type Column = {
  key: string
  label: string
  value: (event: AiFindingEvent) => string | null
}

/** Colunas opcionais: só aparece a que algum evento preenche. */
const OPTIONAL: Column[] = [
  { key: 'user', label: 'Usuário', value: (e) => e.user_name },
  { key: 'file', label: 'Arquivo', value: (e) => e.file_path },
  { key: 'registry', label: 'Registro', value: (e) => e.registry_path },
  { key: 'dns', label: 'DNS', value: (e) => e.dns_question },
  {
    key: 'dst',
    label: 'Destino',
    value: (e) => (e.dst_ip === null ? null : e.dst_port === null ? e.dst_ip : `${e.dst_ip}:${e.dst_port}`),
  },
]

function EventsTable({ events, onOpen }: { events: readonly AiFindingEvent[]; onOpen: (event: AiFindingEvent) => void }) {
  const columns = useMemo(() => OPTIONAL.filter((c) => events.some((e) => c.value(e) !== null)), [events])
  const hasCommand = events.some((e) => e.process_command_line !== null)
  if (events.length === 0) return null

  return (
    <section>
      <h3 className="mb-1 text-micro font-medium uppercase tracking-label text-fg-secondary">Eventos citados</h3>
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th className={TH}>Hora</th>
              <th className={TH}>Citado como</th>
              <th className={TH}>Tipo</th>
              <th className={TH}>Processo</th>
              {hasCommand && <th className={TH}>Linha de comando</th>}
              {columns.map((c) => (
                <th key={c.key} className={TH}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr
                key={`${event.log_id}-${event.event_key}`}
                className={`${TR} cursor-pointer`}
                tabIndex={0}
                onClick={() => onOpen(event)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onOpen(event)
                }}
              >
                <td className={`${TD} tnum font-mono whitespace-nowrap`}>{formatClock(event.event_time)}</td>
                <td className={`${TD} whitespace-nowrap`}>
                  <CitationBadges citedAs={event.cited_as} stacked />
                </td>
                <td className={`${TD} font-mono whitespace-nowrap`}>
                  {event.event_type}
                  {event.action !== null && <span className="text-fg-muted"> · {event.action}</span>}
                </td>
                <td className={`${TD} font-mono text-fg`}>
                  {event.process_name ?? ''}
                  {event.process_pid !== null && <span className="text-fg-muted"> ({event.process_pid})</span>}
                </td>
                {hasCommand && (
                  <td className={TD}>
                    {event.process_command_line !== null && (
                      <code
                        title={event.process_command_line}
                        className="line-clamp-3 block max-w-md min-w-64 font-mono text-micro break-all whitespace-pre-wrap text-fg-secondary"
                      >
                        {event.process_command_line}
                      </code>
                    )}
                  </td>
                )}
                {columns.map((c) => (
                  <td key={c.key} className={TD} title={c.value(event) ?? undefined}>
                    <span className="block max-w-64 truncate font-mono">{c.value(event) ?? ''}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
